import { partService } from '../services/partService';
import { serviceService } from '../services/serviceService';

import { effectiveServiceCode } from './serviceCodeDisplay';

export const CATALOG_SEARCH_LIMIT = 100;

export function isCatalogSearchReady(query) {
  return String(query || '').trim().length >= 1;
}

export async function searchActiveParts(query) {
  const search = String(query || '').trim();
  if (!isCatalogSearchReady(search)) return [];
  const res = await partService.getAll({
    search,
    limit: CATALOG_SEARCH_LIMIT,
    activeOnly: true
  });
  return res.data || [];
}

export async function fetchActivePartById(id) {
  if (id == null || String(id).trim() === '') return null;
  try {
    const res = await partService.getById(id);
    const part = res.data;
    if (part && part.is_active === false) return part;
    return part || null;
  } catch {
    return null;
  }
}

export async function searchActiveServices(query) {
  const search = String(query || '').trim();
  if (!isCatalogSearchReady(search)) return [];
  const res = await serviceService.getAll({
    search,
    limit: CATALOG_SEARCH_LIMIT,
    activeOnly: true
  });
  return res.data || [];
}

export async function fetchActiveServiceById(id) {
  if (id == null || String(id).trim() === '') return null;
  try {
    const res = await serviceService.getById(id);
    const service = res.data;
    if (service && service.is_active === false) return service;
    return service || null;
  } catch {
    return null;
  }
}

/** Parallel part + service search for combined line pickers. */
export async function searchCatalogRefs(query) {
  const search = String(query || '').trim();
  if (!isCatalogSearchReady(search)) {
    return { parts: [], services: [] };
  }
  const [partsRes, servicesRes] = await Promise.all([
    partService.getAll({ search, limit: CATALOG_SEARCH_LIMIT, activeOnly: true }),
    serviceService.getAll({ search, limit: CATALOG_SEARCH_LIMIT, activeOnly: true })
  ]);
  return {
    parts: partsRes.data || [],
    services: servicesRes.data || []
  };
}

export function mergePartSearchResults(results, pinnedPart) {
  if (!pinnedPart) return results;
  const id = String(pinnedPart.id);
  if (results.some((p) => String(p.id) === id)) return results;
  return [pinnedPart, ...results];
}

export function mergeServiceSearchResults(results, pinnedService) {
  if (!pinnedService) return results;
  const id = String(pinnedService.id);
  if (results.some((s) => String(s.id) === id)) return results;
  return [pinnedService, ...results];
}

export function catalogRecordPart(record) {
  if (!record) return null;
  if (record.kind === 'part') return record.data;
  if (record.kind === 'service') return null;
  if (record.part_number != null || record.part_description != null || record.description != null) {
    return record;
  }
  return null;
}

export function catalogRecordService(record) {
  if (!record) return null;
  if (record.kind === 'service') return record.data;
  if (record.kind === 'part') return null;
  if (record.service_description != null || record.service_code != null) {
    return record;
  }
  return null;
}

/** Apply a part or service catalog pick onto a draft line row. */
export function applyCatalogRecordToLineRow(row, catalogRecord) {
  const part = catalogRecordPart(catalogRecord);
  if (part) {
    return {
      ...row,
      item_type: 'part',
      part_id: String(part.id),
      service_id: '',
      part_number: part.part_number ?? '',
      service_code: '',
      description: part.part_description || part.description || '',
      unit_price: part.unit_price || 0,
      unit: part.unit || 'EA'
    };
  }
  const service = catalogRecordService(catalogRecord);
  if (service) {
    return {
      ...row,
      item_type: 'service',
      service_id: String(service.id),
      part_id: '',
      part_number: '',
      service_code: effectiveServiceCode(service) || '',
      description: service.service_description || service.description || '',
      unit_price: service.unit_price || 0,
      unit: service.unit || 'HR'
    };
  }
  return row;
}
